import React, { memo, useEffect, forwardRef, useRef, useState, useCallback } from 'react';
import { Turntable } from '@fuxi/turntable-react';
import { BasePlayerRef, Snapshot } from '@/types';

import '@fuxi/turntable-react/style.css';
import styles from './index.module.css';

interface TurnTableProps {
  assetPaths: string[];
  showArrows?: boolean;
  onChange?: (index: number) => void;
  onLoad: () => void;
  onError: (err: any) => void;
  invert?: boolean;
}
const TurnTable = forwardRef<BasePlayerRef, TurnTableProps>(
  ({ assetPaths, onChange, onLoad, onError, invert, showArrows }, ref) => {
    const containerRef = useRef<any>();
    const [assets, setAssets] = useState(assetPaths);
    const [isInvert, setIsInvert] = useState(invert || false);
    const [index, setIndex] = useState(0);
    const curIndexRef = useRef<number>(0);

    useEffect(() => {
      if (!Array.isArray(assetPaths) || !assetPaths || assetPaths.length === 0) {
        onError?.('assetPaths should be an array');
        return;
      }

      setAssets(assetPaths);
      setIndex(0);
      curIndexRef.current = 0;
    }, [assetPaths]);

    const onTurnTableChange = (index) => {
      if (isNaN(index)) {
        return;
      }
      curIndexRef.current = index;
      setIndex(index);
      onChange && onChange(index);
    };
    const onTurntableLoad = useCallback(() => {
      onLoad();
    }, [assetPaths]);

    const takeSnapshot = (): Promise<Snapshot> => {
      return new Promise((resolve, reject) => {
        const snaps = {
          image: assetPaths[curIndexRef.current],
          params: {
            assetPaths: assets,
            curIndex: curIndexRef.current,
            invert,
          },
        } as Snapshot;
        resolve(snaps);
      });
    };

    const onTurntableError = useCallback(
      (e) => {
        console.log(e);
        onError(e);
      },
      [assetPaths],
    );

    React.useImperativeHandle(ref, () => ({
      takeSnapshot: takeSnapshot,
      getElement: () => containerRef.current,
      restoreSnapshot: (snapshot: Snapshot) => {
        const { params } = snapshot;
        const { curIndex: index, assetPaths, invert } = params;

        curIndexRef.current = index;
        setIndex(index);
        setIsInvert(invert);
        setAssets(assetPaths);
      },
    }));

    return (
      <div className={styles['preview-turn-table']} ref={containerRef}>
        <Turntable
          assetPaths={assetPaths}
          showArrows={showArrows}
          onChange={onTurnTableChange}
          onError={onTurntableError}
          onLoad={onTurntableLoad}
          index={index}
          cors="use-credentials"
          invert={isInvert}
        />
      </div>
    );
  },
);
export default TurnTable;
